import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  ReactNode,
} from "react";
import { createPortal } from "react-dom";

export const Tooltip = ({
  element,
  tooltip,
  position = "bottom",
}: {
  element: JSX.Element;
  tooltip: ReactNode;
  position?: "top" | "bottom";
}): JSX.Element => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const showTimer = useRef<number>();
  const hideTimer = useRef<number>();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    return () => {
      if (showTimer.current) window.clearTimeout(showTimer.current);
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
    };
  }, []);

  useLayoutEffect(() => {
    if (tooltipVisible && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      const left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
      const top =
        position === "top"
          ? triggerRect.top - tooltipRect.height - 4
          : triggerRect.bottom + 4;

      setCoords({ top, left });
    }
  }, [tooltipVisible, position]);

  const clearTimers = () => {
    if (showTimer.current) window.clearTimeout(showTimer.current);
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
  };

  const handleShow = () => {
    clearTimers();
    showTimer.current = window.setTimeout(() => setTooltipVisible(true), 80);
  };

  const handleHide = () => {
    clearTimers();
    hideTimer.current = window.setTimeout(() => setTooltipVisible(false), 80);
  };

  const arrowClasses =
    position === "top"
      ? "border-r border-b -translate-y-1"
      : "border-t border-l translate-y-1";

  return (
    <span
      ref={triggerRef}
      className="inline-flex"
      onMouseEnter={handleShow}
      onMouseLeave={handleHide}
      onFocus={handleShow}
      onBlur={handleHide}
    >
      {element}

      {tooltipVisible &&
        createPortal(
          <span
            ref={tooltipRef}
            style={{ top: coords.top, left: coords.left }}
            className={`pointer-events-none fixed z-50 flex items-center whitespace-nowrap ${
              position === "top" ? "flex-col" : "flex-col-reverse"
            }`}
          >
            <span className="rounded-2xl border border-slate-200/80 bg-white/95 px-3 py-2 text-xs font-medium text-slate-700 shadow-xl shadow-slate-900/10 backdrop-blur dark:border-white/10 dark:bg-slate-900/95 dark:text-white">
              {tooltip}
            </span>
            <span
              className={`mx-auto block h-3 w-3 rotate-45 border-slate-200/80 bg-white/95 dark:border-white/10 dark:bg-slate-900/95 ${arrowClasses}`}
            />
          </span>,
          document.body
        )}
    </span>
  );
};

export default Tooltip;
